import { DEPLOYMENT_KEYS } from 'utils/constants'
import { SCHEDULE_PLATFORM_TYPE } from 'utils/constants'
import {
  COHORT_TYPE,
  END_RULE_TYPE,
  END_RULES_COMBINE,
  FIXED_DATE_MODE,
  OFFSET_DIRECTION,
  OFFSET_UNIT,
  PLATFORM_TYPE,
  REPEAT_MODE,
  AVAILABILITY_MODE,
} from '../constants'
import { TIMEZONE_MODE } from '../constants/timezone'
import { createLocalId } from '../models/ids'
import { createEmptyAvailability } from '../models/createEmptyAvailability'
import { collectExpireVisits } from 'utils/chainExpiryUtils'

export const mapPlatformToLegacy = platformType => {
  if (platformType === PLATFORM_TYPE.web) return SCHEDULE_PLATFORM_TYPE.web
  if (platformType === PLATFORM_TYPE.mobile) return SCHEDULE_PLATFORM_TYPE.mobile
  return 'all'
}

export const mapPlatformFromLegacy = platformType => {
  if (platformType === SCHEDULE_PLATFORM_TYPE.mobile) return PLATFORM_TYPE.mobile
  if (platformType === SCHEDULE_PLATFORM_TYPE.web) return PLATFORM_TYPE.web
  return PLATFORM_TYPE.both
}

const AUDIENCE_COHORT_TYPES = new Set([
  COHORT_TYPE.allParticipants,
  COHORT_TYPE.allCaregivers,
  COHORT_TYPE.allUsers,
  'all_participants',
  'all_caregivers',
  'all_users',
])

export const deriveAudienceType = (cohort = []) => {
  const audienceRow = cohort.find(entry => AUDIENCE_COHORT_TYPES.has(entry?.type))
  if (audienceRow?.type) return audienceRow.type
  if (cohort.length === 1 && (cohort[0]?.type === COHORT_TYPE.allParticipants || cohort[0]?.type === 'all_participants')) {
    return 'all_participants'
  }
  return 'all'
}

export const formatAbsoluteAt = (dateValue, timeOfDay = '09:00') => {
  if (!dateValue) return undefined
  if (String(dateValue).includes('T')) {
    const value = String(dateValue)
    return value.endsWith('Z') ? value : `${value}Z`
  }
  return `${dateValue}T${timeOfDay}:00Z`
}

const API_DATE_KIND = {
  aDay: 'a_day',
  dayOfYear: 'day_of_year',
  weekOfYear: 'week_of_year',
}

export const fixedDateModeToDateKind = mode => {
  if (mode === FIXED_DATE_MODE.dayOfYear) return API_DATE_KIND.dayOfYear
  if (mode === FIXED_DATE_MODE.weekOfYear) return API_DATE_KIND.weekOfYear
  return API_DATE_KIND.aDay
}

export const dateKindToFixedDateMode = dateKind => {
  if (dateKind === API_DATE_KIND.dayOfYear || dateKind === FIXED_DATE_MODE.dayOfYear) {
    return FIXED_DATE_MODE.dayOfYear
  }
  if (dateKind === API_DATE_KIND.weekOfYear || dateKind === FIXED_DATE_MODE.weekOfYear) {
    return FIXED_DATE_MODE.weekOfYear
  }
  if (dateKind === API_DATE_KIND.aDay || dateKind === FIXED_DATE_MODE.day || dateKind === 'day') {
    return FIXED_DATE_MODE.day
  }
  return FIXED_DATE_MODE.day
}

const calendarDayValueFromApi = raw => {
  if (!raw) return ''
  const text = String(raw)
  if (text.includes('T')) return text.slice(0, 10)
  return text
}

export const mapFixedDateFromApi = apiAnchor => {
  const declaredKind =
    apiAnchor?.date_kind || apiAnchor?.fixed_date?.mode || apiAnchor?.date_mode || ''
  let mode = dateKindToFixedDateMode(declaredKind)

  if (!declaredKind) {
    if (apiAnchor?.day_of_year != null && apiAnchor?.day_of_year !== '') {
      mode = FIXED_DATE_MODE.dayOfYear
    } else if (apiAnchor?.week_of_year != null && apiAnchor?.week_of_year !== '') {
      mode = FIXED_DATE_MODE.weekOfYear
    }
  }

  if (mode === FIXED_DATE_MODE.dayOfYear) {
    const raw = apiAnchor?.day_of_year ?? apiAnchor?.fixed_date?.value ?? apiAnchor?.date_value ?? ''
    return { mode, value: raw === '' || raw == null ? '' : String(raw) }
  }

  if (mode === FIXED_DATE_MODE.weekOfYear) {
    const raw = apiAnchor?.week_of_year ?? apiAnchor?.fixed_date?.value ?? apiAnchor?.date_value ?? ''
    return { mode, value: raw === '' || raw == null ? '' : String(raw) }
  }

  const raw =
    apiAnchor?.date ||
    apiAnchor?.absolute_at ||
    apiAnchor?.fixed_date?.value ||
    apiAnchor?.date_value ||
    ''
  return { mode: FIXED_DATE_MODE.day, value: calendarDayValueFromApi(raw) }
}

export const mapAbsoluteAnchorToApi = (anchor, timeOfDay) => {
  const mode = anchor?.fixedDate?.mode || FIXED_DATE_MODE.day
  const date_kind = fixedDateModeToDateKind(mode)
  const value = anchor?.fixedDate?.value

  if (mode === FIXED_DATE_MODE.dayOfYear) {
    const day_of_year = value === '' || value == null ? undefined : Number(value)
    return {
      type: 'absolute',
      date_kind,
      ...(day_of_year != null && !Number.isNaN(day_of_year) ? { day_of_year } : {}),
    }
  }

  if (mode === FIXED_DATE_MODE.weekOfYear) {
    const week_of_year = value === '' || value == null ? undefined : Number(value)
    return {
      type: 'absolute',
      date_kind,
      ...(week_of_year != null && !Number.isNaN(week_of_year) ? { week_of_year } : {}),
    }
  }

  return {
    type: 'absolute',
    date_kind,
    absolute_at: formatAbsoluteAt(value, timeOfDay),
  }
}

export const mapVisibleDaysFromAvailability = (availability = {}) => {
  const openFor = availability.openFor || {}
  if (openFor.unit === 'days' && openFor.value) return Number(openFor.value) || 1
  return 1
}

export const mapRepeatToLegacyRecurring = (repeat = {}, endRules = {}, availability = {}) => {
  const occurrenceRule = (endRules.rules || []).find(rule => rule.type === END_RULE_TYPE.afterNOccurrences)
  const recurrences = repeat.recurrences ?? occurrenceRule?.count

  if (repeat.mode === REPEAT_MODE.everyNDays) {
    if (!recurrences) return undefined
    return {
      interval: Number(repeat.intervalDays) || 1,
      interval_type: 'days',
      scheme: 'n-days',
      visible_days: mapVisibleDaysFromAvailability(availability),
      recurrences: Number(recurrences) || 1,
    }
  }

  if (repeat.mode === REPEAT_MODE.everyNWeeks) {
    if (!recurrences) return undefined
    return {
      interval: Number(repeat.intervalWeeks) || 1,
      interval_type: 'weeks',
      scheme: 'n-weeks',
      visible_days: mapVisibleDaysFromAvailability(availability),
      recurrences: Number(recurrences) || 1,
    }
  }

  if (repeat.mode === REPEAT_MODE.monthlyOnDay) {
    if (!recurrences) return undefined
    return {
      interval: Number(repeat.months) || 1,
      interval_type: 'months',
      scheme: 'n-months',
      visible_days: mapVisibleDaysFromAvailability(availability),
      recurrences: Number(recurrences) || 1,
    }
  }

  return undefined
}

export const mapAvailabilityToLegacyExpire = () => ({ never: null })

/** V3 scheduling-engine contract: `availability` on each schedule (not visibilityRanges). */
export const mapAvailabilityToApi = (availability = {}) => {
  const mode = availability.mode || AVAILABILITY_MODE.fixedDuration
  const openFor = availability.openFor || {}
  const daily = availability.dailyWindow || {}
  const payload = { mode }

  if (mode === AVAILABILITY_MODE.fixedDuration) {
    payload.open_for = {
      value: Number(openFor.value) || 2,
      unit: openFor.unit || 'hours',
    }
  }

  if (daily.enabled === true) {
    const defaults = createEmptyAvailability().dailyWindow
    const startLocal = daily.from || daily.start_local || defaults.from
    const endLocal = daily.until || daily.end_local || defaults.until
    payload.daily_window = {
      enabled: true,
      start_local: startLocal,
      end_local: endLocal,
      timezone: daily.timezone || defaults.timezone || TIMEZONE_MODE.participantLocal,
    }
  }

  return payload
}

export const mapLegacyRepeat = (legacy = {}, baseRepeat) => {
  const recurring = legacy.recurring
  if (!recurring || !recurring.recurrences) {
    return { ...baseRepeat, mode: REPEAT_MODE.doesNotRepeat }
  }
  if (recurring.scheme === 'n-days' && recurring.interval) {
    return {
      ...baseRepeat,
      mode: REPEAT_MODE.everyNDays,
      intervalDays: Number(recurring.interval) || 1,
      recurrences: Number(recurring.recurrences) || baseRepeat.recurrences,
    }
  }
  if (recurring.scheme === 'n-months' && recurring.interval) {
    return {
      ...baseRepeat,
      mode: REPEAT_MODE.monthlyOnDay,
      months: Number(recurring.interval) || 1,
      recurrences: Number(recurring.recurrences) || baseRepeat.recurrences,
    }
  }
  return { ...baseRepeat, mode: REPEAT_MODE.doesNotRepeat }
}

const mapLegacyChainExpiryEndRule = (legacy = {}) => {
  const expiry = legacy.chain_expiry_info
  if (!expiry?.chain_expiry_by_ss_visit && !expiry?.expiry_condition?.terms?.length) return null

  const visits = collectExpireVisits(expiry)
  const byArm = {}
  visits.forEach(visit => {
    const armKey = visit.study_schedule_id
    if (armKey && visit.visit_id) {
      byArm[String(armKey)] = { visit_id: String(visit.visit_id) }
    }
  })

  const terms = expiry?.expiry_condition?.terms || []
  const termOffset = terms.find(term => term.days_offset != null)?.days_offset
  const offsetAmount = termOffset != null ? Math.abs(Number(termOffset)) : 7

  return {
    id: createLocalId('end_rule'),
    type: END_RULE_TYPE.relativeToVisit,
    offset: { amount: offsetAmount, unit: OFFSET_UNIT.days, direction: OFFSET_DIRECTION.after },
    count: 12,
    byArm,
  }
}

export const mapEndRulesToApi = (endRules = {}) => {
  const rules = (endRules.rules || [])
    .filter(rule => rule?.type)
    .map(rule => {
      const payload = { type: rule.type }
      if (rule.type === END_RULE_TYPE.relativeToVisit) {
        payload.offset = {
          value: rule.offset?.amount ?? rule.offset?.value ?? 0,
          unit: rule.offset?.unit || 'days',
          direction: rule.offset?.direction || 'after',
        }
        const by_arm = {}
        Object.entries(rule.byArm || {}).forEach(([armKey, entry]) => {
          const visitId = entry?.visit_id || entry?.visitId
          if (visitId) by_arm[armKey] = { visit_id: String(visitId) }
        })
        if (Object.keys(by_arm).length) payload.by_arm = by_arm
      }
      if (rule.type === END_RULE_TYPE.afterNOccurrences) {
        payload.count = Number(rule.count) || 1
      }
      return payload
    })

  if (!rules.length) return undefined
  return {
    combine: endRules.combine || END_RULES_COMBINE.earliestOf,
    rules,
  }
}

export const mapLegacyEndRules = (legacy = {}, baseEndRules) => {
  const recurring = legacy.recurring
  const expire = legacy.expire || {}
  const deploy = legacy.deploy || {}
  const rules = []

  const relativeRule = mapLegacyChainExpiryEndRule(legacy)
  if (relativeRule) rules.push(relativeRule)

  if (recurring?.recurrences) {
    rules.push({
      id: createLocalId('end_rule'),
      type: END_RULE_TYPE.afterNOccurrences,
      count: Number(recurring.recurrences) || 1,
      offset: { amount: 0, unit: OFFSET_UNIT.days, direction: OFFSET_DIRECTION.after },
      byArm: {},
    })
  }

  if (DEPLOYMENT_KEYS.firstLogin in expire && DEPLOYMENT_KEYS.firstLogin in deploy) {
    const duration = Number(expire.first_login.interval) - Number(deploy.first_login.interval)
    if (duration >= 0 && !recurring?.recurrences) {
      return {
        combine: baseEndRules.combine,
        rules,
      }
    }
  }

  const expiryOperator = legacy.chain_expiry_info?.expiry_condition?.operator
  const combineFromExpiry =
    expiryOperator === 'AND' ? END_RULES_COMBINE.latestOf : END_RULES_COMBINE.earliestOf

  return {
    combine: rules.length > 1 ? combineFromExpiry : baseEndRules.combine,
    rules,
  }
}

export const mapLegacyAvailability = (legacy = {}, baseAvailability) => {
  const expire = legacy.expire || {}
  const deploy = legacy.deploy || {}
  const visibility = legacy.visibility
  const visibilityRanges = legacy.visibilityRanges || []
  const range = visibilityRanges[0] || {}
  const recurring = legacy.recurring || {}

  let openFor = { ...baseAvailability.openFor }
  if (DEPLOYMENT_KEYS.firstLogin in expire && DEPLOYMENT_KEYS.firstLogin in deploy) {
    const days = Number(expire.first_login.interval) - Number(deploy.first_login.interval)
    if (days >= 0) {
      openFor = { value: days || 24, unit: days ? 'days' : 'hours' }
    }
  } else if (recurring.visible_days) {
    openFor = { value: Number(recurring.visible_days) || 1, unit: 'days' }
  }

  const dailyWindow = { ...baseAvailability.dailyWindow }
  if (visibility === 'specific_time' && (range.start || range.end)) {
    dailyWindow.enabled = true
    dailyWindow.from = range.start || dailyWindow.from
    dailyWindow.until = range.end || dailyWindow.until
  }

  return {
    ...baseAvailability,
    openFor,
    dailyWindow,
  }
}
